
import { Component, Prop, Vue, Ref, Watch } from 'vue-property-decorator';
import ProcessStepManager, { ProcessStep } from './ProcessStepManager.vue';
import ProcessWizardSidebar, { StepInfo, ActionButton } from './ProcessWizardSidebar.vue';
import StepSectionHeader from '@/components/forms/form-wizard/eicrapro/shared/StepSectionHeader.vue';
import {ProcessWizardVideo} from "@/components/forms/templates/ProcessWizardSidebar.vue";

// Dropdown option interface
export interface DropdownOption {
  label: string;
  description?: string;
  icon?: string;
  handler: () => Promise<void> | void;
  disabled?: boolean | (() => boolean);
}

// Action button interface (used by ProcessWizardSidebar)
export interface ActionButton {
  label: string;
  handler?: () => Promise<void> | void;
  variant?: 'primary' | 'secondary' | 'destructive';
  disabled?: boolean | (() => boolean);
  tooltip?: string | (() => string);
  
  // Dropdown support
  hasDropdown?: boolean;
  dropdownOptions?: DropdownOption[];
}

export interface WizardStep {
  id: string;
  title: string;
  description?: string;
  eyebrow?: string;
  badge?: string;
  helper?: string;
  hideHeader?: boolean; // If true, don't auto-render header (for custom headers)
  canContinue?: boolean | ((state: any) => boolean);
  primaryAction?: {
    label: string;
    // Handler can optionally return a boolean to control auto-advance
    handler: (state: any) => boolean | void | Promise<boolean | void>;
    variant?: 'primary' | 'secondary' | 'destructive';
    
    // Dropdown support
    hasDropdown?: boolean;
    dropdownOptions?: DropdownOption[];
  };
  secondaryActions?: Array<{
    label: string;
    // Secondary actions may also return boolean, but ProcessStepManager
    // currently ignores the result (used mainly for side effects)
    handler: (state: any) => boolean | void | Promise<boolean | void>;
    variant?: 'primary' | 'secondary' | 'destructive';
    disabled?: boolean | ((state: any) => boolean);
    tooltip?: string | (() => string);
    
    // Dropdown support
    hasDropdown?: boolean;
    dropdownOptions?: DropdownOption[];
  }>;
}


/**
 * ProcessWizardTemplate - Flexible multi-step wizard component
 *
 * Combines ProcessStepManager and ProcessSidebar with automatic header rendering.
 * Step information (title, eyebrow, badge) is automatically shown in both sidebar and content area.
 *
 * Time: O(n) where n = number of steps
 * Space: O(n)
 */
@Component({
  components: {
    ProcessStepManager,
    ProcessWizardSidebar,
    StepSectionHeader
  }
})
export default class ProcessWizardTemplate extends Vue {
  @Ref('stepManager')
  readonly stepManager!: ProcessStepManager;

  @Prop({ type: Array, required: true })
  steps!: WizardStep[];

  @Prop({ type: Array, default: () => [] })
  videos!: ProcessWizardVideo[];

  @Prop({ type: String })
  warningMessage?: string;

  @Prop({ type: String })
  pageName?: string;

  @Prop({ type: Boolean, default: false })
  isProcessing!: boolean;

  @Prop({ type: String })
  processingMessage?: string;

  @Prop({ type: Object, default: () => ({}) })
  modelValue!: Record<string, any>;

  // Track current step index for reactivity
  currentStepIndexValue: number = 1;

  @Watch('stepManager.currentStep', { immediate: true, deep: true })
  onStepManagerCurrentStepChange(newStep: number): void {
    // console.log('[ProcessWizardTemplate] onStepManagerCurrentStepChange:', newStep);
    if (newStep) {
      this.currentStepIndexValue = newStep;
    }
  }

  handleStepChange(step: number): void {
    // console.log('[ProcessWizardTemplate] handleStepChange called:', step);
    this.currentStepIndexValue = step;
  }

  /**
   * Convert wizard steps to ProcessStep format
   *
   * Time: O(n)
   * Space: O(n)
   */
  get processSteps(): ProcessStep[] {
    const steps = this.steps;
    if (!steps || steps.length === 0) return [];
    
    return steps.map((step) => {
      return {
        id: step.id,
        title: step.title,
        description: step.description,
        // Direct function reference - no closure caching for reactivity
        canContinue:
          typeof step.canContinue === 'function'
            ? () => {
              try {
                // Access modelValue directly for reactivity
                return (step.canContinue as (state: any) => boolean)(this.modelValue);
              } catch (e) {
                console.error('Error in canContinue function:', e);
                return false;
              }
            }
            : step.canContinue,
        onContinue: step.primaryAction?.handler
          ? async () => {
            try {
              // Access modelValue directly for reactivity
              const result = await step.primaryAction!.handler(this.modelValue);
              // Allow boolean result to control auto-advance in ProcessStepManager
              return result;
            } catch (e) {
              console.error('Error in primaryAction handler:', e);
              return false;
            }
          }
          : undefined
      };
    });
  }

  /**
   * Sidebar steps (display only)
   *
   * Time: O(n)
   * Space: O(n)
   */
  get sidebarSteps(): StepInfo[] {
    return this.steps.map((s) => ({
      id: s.id,
      title: s.title
    }));
  }

  /**
   * Get current step index (1-based)
   *
   * Time: O(1)
   * Space: O(1)
   */
  get currentStepIndex(): number {
    // Use tracked value for reactivity (updated by watch or callback)
    const step = this.currentStepIndexValue || 1;
    // console.log('[ProcessWizardTemplate] currentStepIndex getter:', {
    //   trackedValue: this.currentStepIndexValue,
    //   returning: step
    // });
    return step;
  }

  /**
   * Get current step config
   *
   * Time: O(1)
   * Space: O(1)
   */
  get currentStep(): WizardStep | undefined {
    return this.steps?.[this.currentStepIndex - 1];
  }

  /**
   * Primary action for current step
   *
   * Time: O(1)
   * Space: O(1)
   */
  get currentPrimaryAction(): ActionButton | undefined {
    const step = this.currentStep;
    
    // console.log('[ProcessWizardTemplate] currentPrimaryAction:', {
    //   currentStepIndex: this.currentStepIndex,
    //   stepsLength: this.steps?.length,
    //   step: step,
    //   hasPrimaryAction: !!step?.primaryAction,
    //   primaryAction: step?.primaryAction
    // });
    
    if (!step?.primaryAction) {
      return {
        label: 'Devam Et',
        handler: async () => {
          return true;
        },
        variant: 'primary'
      };
    }

    const primaryAction = step.primaryAction;
    
    return {
      label: primaryAction.label,
      handler: async () => {
        if (primaryAction.handler) {
          return await primaryAction.handler(this.modelValue);
        }
        return false;
      },
      variant: primaryAction.variant || 'primary',
      hasDropdown: primaryAction.hasDropdown,
      dropdownOptions: primaryAction.dropdownOptions
    };
  }

  /**
   * Secondary actions for current step
   *
   * Time: O(k) where k = number of secondary actions
   * Space: O(k)
   */
  get currentSecondaryActions(): ActionButton[] {
    const step = this.currentStep;
    
    if (!step?.secondaryActions) {
      return [];
    }

    return step.secondaryActions.map((action) => ({
      label: action.label,
      handler: () => action.handler(this.modelValue),
      variant: action.variant || 'secondary',
      disabled:
        typeof action.disabled === 'function'
          ? action.disabled(this.modelValue)
          : action.disabled || false,
      hasDropdown: action.hasDropdown,
      dropdownOptions: action.dropdownOptions
    }));
  }

  /**
   * Dedicated Hemen Aç Action Object (first secondary action)
   *
   * Time: O(1)
   * Space: O(1)
   */
  get currentHemenAcAction(): ActionButton | undefined {
    const step = this.currentStep;
    const action = step?.secondaryActions?.[0];
    
    // console.log('[ProcessWizardTemplate] currentHemenAcAction:', {
    //   currentStepIndex: this.currentStepIndex,
    //   stepsLength: this.steps?.length,
    //   step: step,
    //   hasSecondaryActions: !!step?.secondaryActions,
    //   secondaryActionsLength: step?.secondaryActions?.length,
    //   action: action
    // });
    
    if (!action) {
      return undefined;
    }

    return {
      label: action.label,
      handler: async () => {
        if (action.handler) {
          return await action.handler(this.modelValue);
        }
        return false;
      },
      variant: action.variant || 'secondary',
      disabled: typeof action.disabled === 'function' 
        ? action.disabled 
        : () => action.disabled || false,
      tooltip: typeof (action as any).tooltip === 'function'
        ? (action as any).tooltip
        : () => (action as any).tooltip || '',
      hasDropdown: action.hasDropdown,
      dropdownOptions: action.dropdownOptions
    };
  }

  canContinueFromStep(stepNumber: number): boolean {
    // console.log("//////////////// canContinueFromStep ",stepNumber)
    const step = this.steps[stepNumber - 1];
    // console.log("step",step);
    if (!step) return false;
    // console.log("typeof step.canContinue === 'function'",typeof step.canContinue === 'function');
    if (typeof step.canContinue === 'function') {
      // console.log("step.canContinue(this.modelValue)",step.canContinue(this.modelValue));
      return step.canContinue(this.modelValue);
    }
    // console.log("return step.canContinue !== false",step.canContinue !== false);
    return step.canContinue !== false;
  }

  onProcessComplete(): void {
    this.$emit('process-complete', this.modelValue);
  }

  /**
   * Dynamic Hemen Aç handler - called when button is clicked with current step from sidebar props
   * This ensures we use the correct step index at click time
   */
  async handleHemenAcClick(sidebarCurrentStep: number): Promise<void> {
    // console.log('[ProcessWizardTemplate] handleHemenAcClick called:', {
    //   sidebarCurrentStep,
    //   totalSteps: this.steps?.length,
    //   isLastStep: sidebarCurrentStep === (this.steps?.length || 0)
    // });

    const totalSteps = this.steps?.length || 0;
    const isHemenAc = totalSteps > 0 && sidebarCurrentStep === totalSteps;

    if (!isHemenAc) {
      console.error('[ProcessWizardTemplate] Not at last step');
      this.$toast?.error('Hemen Aç işlemi bu adımda mevcut değil');
      return;
    }

    const stepIndex = sidebarCurrentStep - 1;
    const step = this.steps?.[stepIndex];

    if (!step || !step.secondaryActions || step.secondaryActions.length === 0) {
      console.error('[ProcessWizardTemplate] No secondaryActions found');
      this.$toast?.error('Hemen Aç işlemi tanımlı değil');
      return;
    }

    const action = step.secondaryActions[0];
    if (!action || !action.handler) {
      console.error('[ProcessWizardTemplate] Handler missing');
      this.$toast?.error('Hemen Aç işlemi hazır değil');
      return;
    }

    // console.log('[ProcessWizardTemplate] Executing handler:', { label: action.label });
    try {
      await action.handler(this.modelValue);
      // console.log('[ProcessWizardTemplate] Handler completed successfully');
    } catch (error) {
      console.error('[ProcessWizardTemplate] Handler error:', error);
      this.$toast?.error(`Hemen Aç hatası: ${(error as Error).message}`);
    }
  }

  goToStep(stepNumber: number): void {
    if (this.stepManager && stepNumber >= 1 && stepNumber <= this.steps.length) {
      this.stepManager.setCurrentStep(stepNumber);
    }
  }

  updateModelValue(value: any): void {
    this.$emit('update:modelValue', value);
  }
}
